import React from 'react';
import socketIOClient from 'socket.io-client';
import moment from 'moment';
import { connect } from 'react-redux';
import { getServices } from 'app/api/redux/Services/actions';
import { getRecords } from 'app/api/redux/Records/actions';

import { IMessage, IRecordResponse, IService } from 'app/api/types';
import './styles.scss';

import { IChatNotification } from 'app/api/types/ChatNotification';
import getNotifications from 'app/api/requests/Chat/getNotifications';
import getChatToken from 'app/api/getChatToken';
import removeNotification from 'app/api/requests/Chat/removeNotification';
import { socket as chatSocket } from 'app/api/socket';

interface IRecordNotificationsState {
  email: string;
  notifications: IRecordNotification[];
  messages: IMessage[];
  chats: IChatNotification[];
}
interface IRecordNotificationsProps {
  email?: string;
  serviceList: IService[];
  getServices: () => Promise<IService[]>;
  getRecords: (date: string) => Promise<IRecordResponse>;
}

const socket = socketIOClient(`${process.env.REACT_APP_SOCKET_HOST}`);

class RecordNotifications extends React.Component<
  IRecordNotificationsProps,
  IRecordNotificationsState
> {
  constructor(props: IRecordNotificationsProps) {
    super(props);

    this.state = {
      email: props.email!,
      notifications: [],
      messages: [],
      chats: []
    };
  }

  audio = new Audio("https://ledelice.ru/sounds/notification.wav");

  componentDidUpdate(prevProps: IRecordNotificationsProps) {
    if (this.props.email !== prevProps.email) {
      this.setState({ email: this.props.email! }, () =>
        socket.emit("set_user", { email: this.state.email })
      );
    }
  }

  async loadChatNotifications() {
    await chatSocket.waitStarted();
    return await getNotifications(getChatToken());
  }

  componentDidMount() {
    this.props.getServices();

    this.loadChatNotifications().then(chats => this.setState({ chats }));
    chatSocket.onChatNotification((notification) => {
      this.setState({
        chats: [ ...this.state.chats, notification ]
      });
      this.audio.volume = 1;
      this.audio.play();
    });

    socket.on("history", (history: IRecordNotification[]) =>
      this.setState({ notifications: history })
    );

    socket.on("record_notification", (data: IRecordNotification) => {
      this.setState(
        { notifications: [...this.state.notifications, data] },
        () => this.props.getRecords(moment().format("YYYY-MM-DD"))
      );
      this.audio.volume = 1;
      this.audio.play();
    });

    socket.on("message", (data: IMessage) => {
      this.setState({ messages: [...this.state.messages, data] });
      this.audio.volume = 1;
      this.audio.play();
    });
  }

  delNotification = (id: string) => {
    socket.emit("del_notification", { id, email: this.state.email });
    this.setState({
      notifications: this.state.notifications.filter(notif => notif.id !== id)
    });
  };

  delMessage = (id: number) => {
    this.setState({
      messages: this.state.messages.filter(mes => mes.id !== id)
    });
  };

  delChatNotification = (id: number) => {
    this.setState({
      chats: this.state.chats.filter(x => x.id !== id)
    });
    removeNotification(id, getChatToken());
  }

  render() {
    return (
      <div className="record-notifications">
        {this.state.notifications.map((notification: IRecordNotification) => (
          <RecordNotification
            notification={notification}
            serviceList={this.props.serviceList}
            onDelete={this.delNotification}
            key={notification.id}
          />
        ))}
        {this.state.messages.map(message => {
          return (
            <div className="record-notification" key={message.id}>
              <div className="record-notification__content">
                <p>
                  имя: <span>{message.name}</span>
                </p>
                <p>
                  дата:{" "}
                  <span>
                    {moment(message.date).format("YYYY-MM-DD HH:MM:SS")}
                  </span>
                </p>
                <p>
                  сообщение: <span>{message.message}</span>
                </p>
              </div>
              <div
                className="record-notification__close"
                onClick={() => this.delMessage(message.id)}
              >
                x
              </div>
            </div>
          );
        })}
        {this.state.chats.map(notification => {
          return (<ChatNotification
              notification={notification}
              key={notification.id}
              onDelete={this.delChatNotification}
          />)
        })}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    email: state.users.profile.email,
    serviceList: state.services.serviceList
  };
};

export default connect(mapStateToProps, { getServices, getRecords })(
  RecordNotifications
);

interface IRecordNotification {
  id: string;
  master_name: string;
  time_start: string;
  time_end: string;
  service_ids: number[];
  date: string;
  customer_name: string;
  customer_phone: string;
  is_chat_message?: boolean;
  guest_name?: string;
}

interface IRecordNotificationProps {
  onDelete: (id: string) => void;
  serviceList: IService[];
  notification: IRecordNotification;
}

const RecordNotification: React.FC<IRecordNotificationProps> = ({
  notification,
  onDelete,
  serviceList
}) => {
  const isExistingId = (id: number) => {
    return notification.service_ids.indexOf(id) !== -1;
  };

  return (
    <div className="record-notification">
      <div className="record-notification__content">
        <p>
          мастер: <span>{notification.master_name}</span>
        </p>
        <p>
          клиент: <span>{notification.customer_name}</span>
        </p>
        <p>
          телефон: <span>{notification.customer_phone}</span>
        </p>
        <p>
          дата: <span>{moment(notification.date).format("YYYY-MM-DD")}</span>
        </p>
        <p>
          время начала: <span>{notification.time_start}</span>
        </p>
        <p>
          время конца: <span>{notification.time_end}</span>
        </p>
        <p>Услуги:</p>
        {serviceList.map(
          service =>
            isExistingId(Number(service.id!)) && (
              <p key={service.id}>
                <span> - {service.title}</span>
              </p>
            )
        )}
      </div>
      <div
        className="record-notification__close"
        onClick={() => onDelete(notification.id)}
      >
        x
      </div>
    </div>
  );
};

interface IChatNotificationProps {
  notification: IChatNotification;
  onDelete: (id: number) => void;
}

const ChatNotification: React.FC<IChatNotificationProps> = ({
  notification,
  onDelete,
}) => {
  return <div className="record-notification record-notification_chat">
    <div className="record-notification__content">
      <p>имя: <span>{notification.guestName ?? ''}</span></p>
      {notification.guestPhone ? <p>телефон: <span>{notification.guestPhone}</span></p> :
          <p>телефон не указан</p>}
      <p>дата: <span>{moment(notification.time).format("YYYY-MM-DD HH:mm")}</span></p>
    </div>
    <div
        className="record-notification__close"
        onClick={() => onDelete(notification.id)}
    >
      x
    </div>
  </div>;
}
